import { Controller } from "@hotwired/stimulus"
import client from "braintree-web/client"
import hostedFields from "braintree-web/hosted-fields"
import paypalCheckout from "braintree-web/paypal-checkout"
import applePay from "braintree-web/apple-pay"

/**
 * Braintree client integration for the bag checkout.
 *
 * - Hosted Fields (PCI SAQ-A): renders card number / expiration / CVV in
 *   Braintree-hosted iframes. The PAN never touches our origin.
 * - PayPal-Checkout via Braintree: renders a PayPal button that opens PayPal
 *   in a popup and returns a payment_method_nonce on approval.
 * - Apple Pay via Braintree: ApplePaySession sheet -> tokenize -> JSON POST to
 *   /checkout; the sheet completes only after the server authorization
 *   (docs/BRAINTREE_APPLE_PAY_PLAN.md). The option stays hidden until the
 *   capability check passes (bag:checkout:apple-pay-ready).
 * - Forward-compatible with Google Pay / Venmo: the same client instance +
 *   nonce flow is reused; add a new payment-method module later.
 *
 * Server contract:
 *   POST /checkout with order[payment_method_nonce]=<single-use nonce>
 *   The server authorizes cards and Apple Pay, and submits PayPal as a sale.
 *   Apple Pay posts with Accept: application/json and receives
 *   { success, redirect_url | error }.
 */
export default class extends Controller {
  static targets = [
    "number",          // div mount point for card number
    "expirationDate",  // div mount point for expiration MM/YY
    "cvv",             // div mount point for CVV
    "nonce",           // hidden input that will hold the nonce
    "paypalButton",    // div mount point for the PayPal button
    "applePayButton",  // the Apple Pay button inside _payment_ap
    "applePayErrors",  // inline Apple Pay error display inside _payment_ap
    "cardSummary",     // optional element to display "VISA ending 1111" for review
    "cardErrors",      // optional element to display hosted-fields errors
    "processingOverlay", // full-screen "Processing your payment…" overlay for PayPal/Apple Pay
  ]

  static values = {
    tokenUrl:    { type: String, default: "/checkout/client_token" },
    paypalAmount: { type: Number, default: 0 },
    paypalCurrency: { type: String, default: "USD" },
  }

  async connect() {
    this.hostedFieldsInstance = null
    this.paypalCheckoutInstance = null
    this.applePayInstance = null
    this.tokenizePromise = null
    this.boundTotalsUpdated = this._handleTotalsUpdated.bind(this)
    document.addEventListener("bag:checkout:totals-updated", this.boundTotalsUpdated)

    let clientInstance
    try {
      const token = await this._fetchClientToken()
      if (!token) {
        console.error("[braintree] missing client token")
        this._showCardError("Payment form could not be loaded. Please refresh.")
        return
      }
      clientInstance = await client.create({ authorization: token })
    } catch (err) {
      console.error("[braintree] client init error:", err)
      this._showCardError("Payment form could not be loaded. Please refresh.")
      return
    }

    // Hosted Fields (only if the card form is present in the DOM). A failure
    // here is fatal for the card flow — surface it inline.
    if (this.hasNumberTarget && this.hasExpirationDateTarget && this.hasCvvTarget) {
      try {
        await this._setupHostedFields(clientInstance)
      } catch (err) {
        console.error("[braintree] hosted-fields init error:", err)
        this._showCardError("Card form could not be loaded. Please refresh.")
      }
    }

    // PayPal-Checkout (only if the PP mount point is present). Best-effort:
    // a failure must not break the card flow — the PayPal tab will simply
    // not render its button. Operators can disable PayPal in the Braintree
    // dashboard for this merchant; that should not affect card payments.
    if (this.hasPaypalButtonTarget) {
      try {
        await this._setupPaypalCheckout(clientInstance)
      } catch (err) {
        console.warn("[braintree] paypal init failed (card flow unaffected):", err)
        this._showPaypalUnavailable()
      }
    }

    // Apple Pay (only on capable Safari/device combos). Best-effort like
    // PayPal: any failure simply leaves the Apple Pay option hidden — the
    // payment-method card is only revealed by the apple-pay-ready event.
    if (this.hasApplePayButtonTarget) {
      try {
        await this._setupApplePay(clientInstance)
      } catch (err) {
        console.warn("[braintree] apple pay init failed (other methods unaffected):", err)
      }
    }
  }

  disconnect() {
    if (this.hostedFieldsInstance) {
      this.hostedFieldsInstance.teardown().catch(() => {})
      this.hostedFieldsInstance = null
    }
    if (this.paypalCheckoutInstance) {
      this.paypalCheckoutInstance.teardown().catch(() => {})
      this.paypalCheckoutInstance = null
    }
    if (this.applePayInstance) {
      this.applePayInstance.teardown().catch(() => {})
      this.applePayInstance = null
    }
    if (this._paypalResizeObserver) {
      this._paypalResizeObserver.disconnect()
      this._paypalResizeObserver = null
    }
    document.removeEventListener("bag:checkout:totals-updated", this.boundTotalsUpdated)
  }

  // ---------------------------------------------------------------------------
  // Public API consumed by bag/validators/checkout_form_controller.js
  // ---------------------------------------------------------------------------

  /**
   * Returns true when the currently selected payment method requires a fresh
   * Braintree nonce before the order form can be submitted.
   */
  needsTokenize() {
    const selected = this._selectedPaymentType()
    if (selected !== "CC") return false
    if (!this.hasNonceTarget) return false
    return !this.nonceTarget.value
  }

  /**
   * Tokenize the current Hosted Fields card input and write the nonce into
   * the hidden order[payment_method_nonce] field. Resolves true on success,
   * false on validation failure (errors are rendered inline).
   */
  async tokenize() {
    if (!this.hostedFieldsInstance) {
      this._showCardError("Payment form is not ready. Please wait a moment and try again.")
      return false
    }

    if (this.tokenizePromise) return this.tokenizePromise

    this.tokenizePromise = this._doTokenize().finally(() => {
      this.tokenizePromise = null
    })
    return this.tokenizePromise
  }

  async _doTokenize() {
    try {
      const payload = await this.hostedFieldsInstance.tokenize()
      this._clearCardError()
      this._clearAllFieldErrors()
      this._writeNonce(payload.nonce)
      this._updateCardSummary(payload.details)
      return true
    } catch (err) {
      console.error("[braintree] tokenize error:", err)
      this._showHostedFieldsError(err)
      // Hosted Fields are cross-origin iframes, so the native form validator
      // can't flag them. Surface inline per-field errors (red border +
      // "Required field") to match the rest of the checkout form.
      this._markEmptyOrInvalidFields()
      return false
    }
  }

  // ---------------------------------------------------------------------------
  // Internal setup
  // ---------------------------------------------------------------------------

  async _fetchClientToken() {
    const response = await fetch(this.tokenUrlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
    if (!response.ok) throw new Error(`client_token HTTP ${response.status}`)
    const data = await response.json()
    return data.token
  }

  async _setupHostedFields(clientInstance) {
    if (this.hostedFieldsInstance) {
      await this.hostedFieldsInstance.teardown().catch(() => {})
      this.hostedFieldsInstance = null
    }

    // Stimulus/Turbo can reconnect this controller against DOM that still has
    // stale Braintree iframes in the mount points. Braintree refuses to create
    // Hosted Fields in a non-empty element, so make setup idempotent.
    this._resetHostedFieldContainers()

    const fieldStyle = {
      "input": {
        "font-size": "16px",
        "color": "#212529",
      },
      "::placeholder": {
        "font-size": "14px",
        "color": "#b7babe",
      },
      ":focus": {
        "color": "#681026",
      },
      ".invalid": {
        "color": "#dc3545",
      },
    }

    this.hostedFieldsInstance = await hostedFields.create({
      client: clientInstance,
      styles: fieldStyle,
      fields: {
        number:         { selector: `#${this.numberTarget.id}`,         placeholder: "1234 5678 9012 3456" },
        expirationDate: { selector: `#${this.expirationDateTarget.id}`, placeholder: "MM/YY" },
        cvv:            { selector: `#${this.cvvTarget.id}`,            placeholder: "CVV" },
      },
    })

    this.hostedFieldsInstance.on("validityChange", (event) => {
      this._clearCardError()
      // The nonce field is shared by card / PayPal / Apple Pay. Only invalidate
      // it for card edits — a stray Hosted Fields event must not wipe a nonce
      // that PayPal or Apple Pay just wrote.
      if (this.hasNonceTarget && this.nonceTarget.value && this._selectedPaymentType() === "CC") {
        this.nonceTarget.value = ""
      }
      // Drop the inline error for any field that is now valid & non-empty.
      const field = event.fields[event.emittedBy]
      const el = this._fieldElementFor(event.emittedBy)
      if (el && field && field.isValid && !field.isEmpty) {
        this._clearFieldError(el)
      }
    })

    // Clear the "Required field" message the moment the shopper starts typing.
    this.hostedFieldsInstance.on("notEmpty", (event) => {
      const el = this._fieldElementFor(event.emittedBy)
      if (el) this._clearFieldError(el)
    })

    this.hostedFieldsInstance.on("cardTypeChange", (event) => {
      const card = event.cards[0]
      this._highlightBrand(card?.type)
    })
  }

  async _setupPaypalCheckout(clientInstance) {
    this.paypalCheckoutInstance = await paypalCheckout.create({ client: clientInstance })

    await this.paypalCheckoutInstance.loadPayPalSDK({
      currency: this.paypalCurrencyValue,
      intent: "capture",
    })

    if (!window.paypal || !window.paypal.Buttons) {
      console.error("[braintree] paypal SDK did not load")
      return
    }

    // Build (but don't yet render) the button. PayPal Smart Buttons will not
    // lay out inside a display:none container, and on page load the PayPal tab
    // (#pt-pp) is hidden because Credit Card is the default method. Rendering
    // eagerly here produces an invisible/blank button. So we render lazily the
    // first time the PayPal tab becomes visible.
    this._paypalButtons = window.paypal.Buttons({
      fundingSource: window.paypal.FUNDING.PAYPAL,
      style: { layout: "horizontal", color: "gold", shape: "rect", label: "checkout", height: 45 },

      onClick: (_data, actions) => {
        if (this._validateFormBeforeWallet()) {
          return actions.resolve()
        }
        return actions.reject()
      },

      createOrder: () => {
        const amount = this._currentTotalAmount()
        return this.paypalCheckoutInstance.createPayment({
          flow: "checkout",
          amount: amount,
          currency: this.paypalCurrencyValue,
          intent: "capture",
          enableShippingAddress: false,
        })
      },

      onApprove: async (data) => {
        // Show feedback the instant the popup closes — the tokenize + full-page
        // submit + server Braintree sale + redirect window is otherwise silent.
        // On success/decline the page navigates and discards the overlay; the
        // only non-navigating path (tokenize throw) hides it explicitly below.
        this._showProcessingOverlay()
        try {
          const payload = await this.paypalCheckoutInstance.tokenizePayment(data)
          this._writeNonce(payload.nonce)
          this._setPaymentType("PP")
          const form = this._form()
          if (form) {
            // Bypass the native submit interceptor here: PayPal already
            // approved the payment and produced a nonce, and the normal
            // submit button is intentionally hidden/disabled for PP. Calling
            // requestSubmit() would fire the validator with
            // submitButtonClicked=false and silently drop the submission.
            form.submit()
          }
        } catch (err) {
          this._hideProcessingOverlay()
          console.error("[braintree] paypal tokenize error:", err)
          alert("There was a problem completing your PayPal payment. Please try again.")
        }
      },

      onCancel: () => {
        this._hideProcessingOverlay()
      },
      onError: (err) => {
        this._hideProcessingOverlay()
        console.error("[braintree] paypal button error:", err)
      },
    })

    this._paypalRendered = false

    // The PayPal mount point is hidden at page load for TWO reasons: the
    // checkout accordion keeps the Payment section (step 3) collapsed until
    // Contact + Shipping are done, AND the PayPal tab is only shown when the
    // PP radio is selected. We must render the moment the container actually
    // gains layout — whether that's via the section expanding, the PP tab
    // being selected, or PayPal already being the default. A ResizeObserver
    // catches all three (it fires when the element transitions from 0x0 to a
    // real box); a plain radio-change listener would miss the default-PP case.
    if (this._isElementVisible(this.paypalButtonTarget)) {
      this._renderPaypalButton()
    } else if (typeof ResizeObserver !== "undefined") {
      this._paypalResizeObserver = new ResizeObserver(() => {
        if (this._isElementVisible(this.paypalButtonTarget)) {
          this._renderPaypalButton()
          if (this._paypalRendered) {
            this._paypalResizeObserver.disconnect()
            this._paypalResizeObserver = null
          }
        }
      })
      this._paypalResizeObserver.observe(this.paypalButtonTarget)
    } else {
      // Very old browser fallback: render anyway (may be blank until shown).
      this._renderPaypalButton()
    }
  }

  _renderPaypalButton() {
    if (this._paypalRendered || !this._paypalButtons) return
    this._paypalRendered = true
    this._paypalButtons.render(this.paypalButtonTarget).catch((err) => {
      this._paypalRendered = false
      console.error("[braintree] paypal button render error:", err)
      this._showPaypalUnavailable()
    })
  }

  _isElementVisible(el) {
    return !!(el && el.offsetParent !== null && el.getClientRects().length > 0)
  }

  // ---------------------------------------------------------------------------
  // Apple Pay
  // ---------------------------------------------------------------------------

  // ApplePaySession API version used for the payment sheet. Must be gated by
  // supportsVersion() below — canMakePayments() alone passes on older Apple
  // Pay-capable Safaris whose session constructor would reject this version.
  static APPLE_PAY_VERSION = 3

  async _setupApplePay(clientInstance) {
    // canMakePayments() is the Apple-recommended gate for showing the button;
    // the wallet itself prompts for card setup if needed. supportsVersion is
    // missing on ancient v1-only Safaris — treat that as unsupported too.
    if (!window.ApplePaySession || !window.ApplePaySession.canMakePayments()) return
    if (!window.ApplePaySession.supportsVersion?.(this.constructor.APPLE_PAY_VERSION)) return

    this.applePayInstance = await applePay.create({ client: clientInstance })

    // Reveal the (server-rendered, hidden) Apple Pay payment-method card.
    // bag--payment-method listens for this and also uses it to gate
    // server-driven availability updates on unsupported browsers.
    document.dispatchEvent(new CustomEvent("bag:checkout:apple-pay-ready"))
  }

  /**
   * Click handler for the Apple Pay button in _payment_ap.html.erb.
   * Everything up to session.begin() must run synchronously inside the user
   * gesture — Safari refuses to open the sheet otherwise. Merchant validation
   * and tokenization happen later inside the session callbacks.
   */
  startApplePay() {
    if (!this.applePayInstance || !window.ApplePaySession) {
      this._showApplePayError("Apple Pay is not available. Please choose another payment method.")
      return
    }

    this._clearApplePayError()

    if (!this._validateFormBeforeWallet()) return

    const paymentRequest = this.applePayInstance.createPaymentRequest({
      total: {
        label: "25karats.com",
        amount: this._currentTotalAmount(),
      },
    })

    // The setup gate checked supportsVersion, but construction can still
    // reject (e.g. a malformed payment request) — fail inline, not uncaught.
    let session
    try {
      session = new window.ApplePaySession(this.constructor.APPLE_PAY_VERSION, paymentRequest)
    } catch (err) {
      console.error("[braintree] apple pay session construction failed:", err)
      this._showApplePayError("Apple Pay is not available on this device. Please choose another payment method.")
      return
    }

    session.onvalidatemerchant = async (event) => {
      try {
        const merchantSession = await this.applePayInstance.performValidation({
          validationURL: event.validationURL,
          displayName: "25karats.com",
        })
        session.completeMerchantValidation(merchantSession)
      } catch (err) {
        console.error("[braintree] apple pay merchant validation error:", err)
        session.abort()
        this._showApplePayError("Apple Pay could not be started. Please try again or choose another payment method.")
      }
    }

    // Apple allows ~30s here before the sheet times out — enough for one
    // tokenize + server-side Braintree authorization. The sheet only shows
    // its success state after the server confirms, per the JSON contract.
    session.onpaymentauthorized = async (event) => {
      let payload
      try {
        payload = await this.applePayInstance.tokenize({ token: event.payment.token })
      } catch (err) {
        console.error("[braintree] apple pay tokenize error:", err)
        session.completePayment(window.ApplePaySession.STATUS_FAILURE)
        this._showApplePayError("There was a problem confirming your payment. You have not been charged. Please try again.")
        return
      }

      // NOTE: the nonce is intentionally NOT written into the shared hidden
      // field — the JSON POST carries it. A leftover single-use Apple Pay
      // nonce in the field would let a later card submit skip tokenization
      // (needsTokenize() checks field presence) and post a dead nonce.
      const result = await this._submitApplePayOrder(payload.nonce)

      if (result.success) {
        session.completePayment(window.ApplePaySession.STATUS_SUCCESS)
        // Cover the gap between the sheet closing and /confirmation rendering.
        this._showProcessingOverlay()
        window.location.href = result.redirectUrl || "/confirmation"
      } else {
        session.completePayment(window.ApplePaySession.STATUS_FAILURE)
        this._showApplePayError(result.error)
      }
    }

    session.begin()
  }

  /**
   * POSTs the full checkout form through the Apple Pay JSON contract:
   * { success: true, redirect_url } | { success: false, error }.
   * Never throws — every failure normalizes to { success: false, error }.
   */
  async _submitApplePayOrder(nonce) {
    const form = this._form()
    if (!form) {
      return { success: false, error: "Checkout form not found. Please refresh and try again." }
    }

    const formData = new FormData(form)
    formData.set("order[payment_type]", "AP")
    formData.set("order[payment_method_nonce]", nonce)

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

    try {
      const response = await fetch(form.action, {
        method: "POST",
        headers: {
          Accept: "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        body: formData,
        credentials: "same-origin",
      })

      let data = null
      try {
        data = await response.json()
      } catch (_e) {
        // Non-JSON body (proxy/error page); falls through to the generic error.
      }

      if (response.ok && data?.success) {
        return { success: true, redirectUrl: data.redirect_url }
      }
      return {
        success: false,
        error: data?.error || "We could not complete payment right now. Please try again or contact us.",
      }
    } catch (err) {
      console.error("[braintree] apple pay checkout POST failed:", err)
      return { success: false, error: "Network error while completing payment. Please check your connection and try again." }
    }
  }

  _showApplePayError(message) {
    if (!this.hasApplePayErrorsTarget) {
      alert(message)
      return
    }
    this.applePayErrorsTarget.textContent = message
    this.applePayErrorsTarget.style.display = "block"
  }

  _clearApplePayError() {
    if (!this.hasApplePayErrorsTarget) return
    this.applePayErrorsTarget.textContent = ""
    this.applePayErrorsTarget.style.display = "none"
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _form() {
    // closest() returns the element itself if it matches, so this covers both
    // "controller is on the <form>" and "controller is inside a <form>"; the
    // querySelector fallback covers "controller wraps a <form>".
    return this.element.closest("form") || this.element.querySelector("form")
  }

  _selectedPaymentType() {
    const radio = document.querySelector('input[name="order[payment_type]"]:checked')
    return radio?.value
  }

  _setPaymentType(value) {
    const radios = document.querySelectorAll('input[name="order[payment_type]"]')
    radios.forEach((r) => {
      if (r.value === value) {
        r.checked = true
        r.dispatchEvent(new Event("change", { bubbles: true }))
      }
    })
  }

  _writeNonce(nonce) {
    if (this.hasNonceTarget) {
      this.nonceTarget.value = nonce
    }
  }

  _currentTotalAmount() {
    const totalEl = document.querySelector("[data-order-total]")
    const raw = this.paypalAmountValue || totalEl?.dataset.orderTotal || 0
    const num = Number(raw)
    return Number.isFinite(num) && num > 0 ? num.toFixed(2) : "0.00"
  }

  _handleTotalsUpdated(event) {
    const total = Number(event.detail?.totalAmount)
    if (Number.isFinite(total) && total > 0) {
      this.paypalAmountValue = total
    }
  }

  // Shared pre-flight for wallet buttons (PayPal, Apple Pay): the contact +
  // shipping/billing sections must validate and the total must be ready
  // before the external wallet UI opens.
  _validateFormBeforeWallet() {
    const form = this._form()
    if (!form) return false

    const validator = form._nativeValidator
    if (validator && !validator.validateVisibleFields()) {
      return false
    }

    const amount = Number(this._currentTotalAmount())
    if (!Number.isFinite(amount) || amount <= 0) {
      alert("Order total is not ready yet. Please review your shipping information and try again.")
      return false
    }

    return true
  }

  _updateCardSummary(details) {
    if (!this.hasCardSummaryTarget || !details) return
    const brand = details.cardType || ""
    const last4 = details.lastFour || ""
    this.cardSummaryTarget.textContent = brand ? `${brand} ending ${last4}` : `Card ending ${last4}`
  }

  _highlightBrand(type) {
    const images = document.querySelectorAll("[data-card-brand]")
    if (!images.length) return
    if (!type) {
      images.forEach((img) => { img.style.opacity = "0.4" })
      return
    }
    images.forEach((img) => {
      img.style.opacity = img.dataset.cardBrand === type ? "1" : "0.25"
    })
  }

  _showHostedFieldsError(err) {
    if (err?.code === "HOSTED_FIELDS_FIELDS_EMPTY") {
      this._showCardError("Please enter your credit card details.")
    } else if (err?.code === "HOSTED_FIELDS_FIELDS_INVALID") {
      const invalid = (err.details?.invalidFieldKeys || []).join(", ")
      this._showCardError(`Please correct the following: ${invalid || "card details"}.`)
    } else if (err?.code === "HOSTED_FIELDS_FAILED_TOKENIZATION") {
      this._showCardError("Your card information could not be verified. Please check and try again.")
    } else if (err?.code === "HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR") {
      this._showCardError("Network error while verifying card. Please try again.")
    } else {
      this._showCardError("There was a problem with your card details. Please try again.")
    }
  }

  _showCardError(message) {
    if (!this.hasCardErrorsTarget) return
    this.cardErrorsTarget.textContent = message
    this.cardErrorsTarget.style.display = "block"
  }

  _clearCardError() {
    if (!this.hasCardErrorsTarget) return
    this.cardErrorsTarget.textContent = ""
    this.cardErrorsTarget.style.display = "none"
  }

  // ---------------------------------------------------------------------------
  // Inline per-field validation for the Hosted Fields
  //
  // The Hosted Fields render inside cross-origin Braintree iframes, so the
  // native checkout validator (which only walks input/select/textarea) can't
  // flag them. These helpers mirror that validator's behaviour — toggling
  // `.is-invalid` on the field container and rendering an `.invalid-feedback`
  // message inside its `.fv-row` — so empty/invalid card fields look exactly
  // like every other required field on the page.
  // ---------------------------------------------------------------------------

  _hostedFieldConfig() {
    return {
      number:         { el: this.hasNumberTarget ? this.numberTarget : null,                 label: "card number" },
      expirationDate: { el: this.hasExpirationDateTarget ? this.expirationDateTarget : null, label: "expiration date" },
      cvv:            { el: this.hasCvvTarget ? this.cvvTarget : null,                        label: "CVV" },
    }
  }

  _fieldElementFor(key) {
    return this._hostedFieldConfig()[key]?.el || null
  }

  _resetHostedFieldContainers() {
    Object.values(this._hostedFieldConfig()).forEach(({ el }) => {
      if (!el) return

      while (el.firstChild) {
        el.removeChild(el.firstChild)
      }
      this._clearFieldError(el)
    })
  }

  _markEmptyOrInvalidFields() {
    if (!this.hostedFieldsInstance) return

    let state
    try {
      state = this.hostedFieldsInstance.getState()
    } catch (_e) {
      return
    }

    const config = this._hostedFieldConfig()
    let firstBadKey = null

    Object.entries(config).forEach(([key, { el, label }]) => {
      if (!el) return
      const field = state.fields[key]
      if (!field) return

      if (field.isEmpty) {
        this._setFieldError(el, "Required field")
        firstBadKey = firstBadKey || key
      } else if (!field.isValid) {
        this._setFieldError(el, `Please enter a valid ${label}`)
        firstBadKey = firstBadKey || key
      } else {
        this._clearFieldError(el)
      }
    })

    // Guide the shopper straight to the first problem field.
    if (firstBadKey) {
      const el = this._fieldElementFor(firstBadKey)
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" })
      try { this.hostedFieldsInstance.focus(firstBadKey) } catch (_e) { /* noop */ }
    }
  }

  _setFieldError(el, message) {
    el.classList.add("is-invalid")
    const row = el.closest(".fv-row") || el.parentElement
    if (!row) return
    let feedback = row.querySelector(".invalid-feedback")
    if (!feedback) {
      feedback = document.createElement("div")
      feedback.className = "invalid-feedback"
      feedback.setAttribute("role", "alert")
      row.appendChild(feedback)
    }
    feedback.textContent = message
    feedback.style.display = "block"
  }

  _clearFieldError(el) {
    el.classList.remove("is-invalid")
    const row = el.closest(".fv-row") || el.parentElement
    const feedback = row && row.querySelector(".invalid-feedback")
    if (feedback) feedback.style.display = "none"
  }

  _clearAllFieldErrors() {
    Object.values(this._hostedFieldConfig()).forEach(({ el }) => {
      if (el) this._clearFieldError(el)
    })
  }

  _showPaypalUnavailable() {
    if (!this.hasPaypalButtonTarget) return
    this.paypalButtonTarget.innerHTML =
      '<div class="alert alert-warning small mb-0">PayPal is currently unavailable. Please choose another payment method.</div>'
  }

  _showProcessingOverlay() {
    if (!this.hasProcessingOverlayTarget) return
    this.processingOverlayTarget.hidden = false
    this.processingOverlayTarget.setAttribute("aria-busy", "true")
  }

  _hideProcessingOverlay() {
    if (!this.hasProcessingOverlayTarget) return
    this.processingOverlayTarget.hidden = true
    this.processingOverlayTarget.setAttribute("aria-busy", "false")
  }
}
